import { DoubleSide } from 'three/src/constants.js';
import { MeshPhongMaterial } from 'three/src/materials/MeshPhongMaterial.js';
import { Mesh } from 'three/src/objects/Mesh.js';
import {
  parseObjGeometry,
  parseDxfGeometry
} from 'modules/project/ProjectAreaPage/ProjectStopesPage/MineModel/Meshes/geometryLoader';
import {
  centerObject,
  moveToMin,
  removeMesh,
  rotateX
} from './positioning';
import { repairStopeGeometry } from './geometryRepair';

// Bounding-box figures of the geometry as authored in the source file (mine grid, Z-up), captured
// BEFORE the view transforms below. The transforms bake rotateX(-90°) plus recentring into the
// geometry, ending with the bbox centred in X/Z and its base at y=0, so a local point maps back to
// the mine grid as: (x + centerX, centerY - z, y + minZ). Kept so exports can undo the transform.
export interface StopeSourceBounds {
  centerX: number;
  centerY: number;
  minZ: number;
}

export interface StopeModel {
  stopeModel: Mesh;
  sourceBounds: StopeSourceBounds | null;
}

const getExtension = (url: string): string => {
  const path = url.split('?')[0];
  const dotIndex = path.lastIndexOf('.');
  return dotIndex >= 0 ? path.substring(dotIndex).toLowerCase() : '';
};

const computeSourceBounds = (mesh: Mesh): StopeSourceBounds | null => {
  mesh.geometry.computeBoundingBox();
  const box = mesh.geometry.boundingBox;
  if (!box) return null;
  return {
    centerX: (box.min.x + box.max.x) / 2,
    centerY: (box.min.y + box.max.y) / 2,
    minZ: box.min.z,
  };
};

const applyTransformsAndMaterial = (mesh: Mesh): StopeModel => {
  const stopeModel = mesh;
  stopeModel.name = 'Initial Stope';

  // Repair FIRST, before anything else touches the geometry. It has to run before
  // computeVertexNormals() at the end of this function (mergeVertices hashes every attribute, so
  // welding a mesh that already carries per-face normals silently merges nothing), and it is safe to
  // run before computeSourceBounds because dropping zero-area faces reuses existing vertices and
  // welding only collapses coincident positions - the bounding box, and therefore the DUF pour-solid
  // export round trip, is bit-identical either side of it.
  stopeModel.geometry = repairStopeGeometry(stopeModel.geometry);

  const sourceBounds = computeSourceBounds(stopeModel);

  rotateX(stopeModel);
  removeMesh(stopeModel);
  centerObject(stopeModel);
  stopeModel.geometry.center();
  centerObject(stopeModel);
  moveToMin(stopeModel);

  stopeModel.geometry.computeVertexNormals();

  stopeModel.material = new MeshPhongMaterial({
    color: 0xFFFFFF,
    transparent: true,
    opacity: 0.5,
    side: DoubleSide,
    flatShading: true,
  });

  return { stopeModel, sourceBounds };
};

const loadMeshFromUrl = async (url: string): Promise<Mesh | null> => {
  const response = await fetch(url);
  if (!response.ok) return null;
  const fileContents = await response.text();
  const extension = getExtension(url);

  const geometry = extension === '.dxf'
    ? parseDxfGeometry(fileContents)
    : parseObjGeometry(fileContents);

  return geometry ? new Mesh(geometry) : null;
};

const useStopeModel = async (url: string): Promise<StopeModel | undefined> => {
  const stopeModel = await loadMeshFromUrl(url);
  if (!stopeModel) return undefined;
  return applyTransformsAndMaterial(stopeModel);
};

export default useStopeModel;
